// pages/DiditSuccess.tsx
//
// Success/Error page for Didit ID verification
// Automatically closes the tab after 1-2 seconds

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

const DiditSuccess = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status"); // "success" or "error"
  const reason = searchParams.get("reason");
  const [countdown, setCountdown] = useState(2);
  const lang = getLanguage();
  const p = authLabels(lang).kyc?.successPage ?? {};
  const fallback = authLabels("en").kyc?.successPage ?? {};
  const isSuccess = status === "success";
  const isError = status === "error";
  const closingText = (
    p.closingInSeconds ??
    fallback.closingInSeconds ??
    "Closing automatically in {{count}} second(s)..."
  ).replace("{{count}}", String(countdown));

  useEffect(() => {
    void (async () => {
      try {
        const { isNativeCapacitorApp } = await import(
          "@/integrations/clients/carAuction/nativeAuth"
        );
        if (isNativeCapacitorApp()) {
          const { closeBankIdSystemBrowser } = await import(
            "@/lib/native/bankIdBrowser"
          );
          await closeBankIdSystemBrowser();
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          void (async () => {
            try {
              const { isNativeCapacitorApp } = await import(
                "@/integrations/clients/carAuction/nativeAuth"
              );
              if (isNativeCapacitorApp()) {
                window.location.hash = "#/dashboard";
                return;
              }
            } catch {
              /* ignore */
            }
            window.close();
          })();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isSuccess]);

  const errorMessage =
    reason === "ssn_mismatch"
      ? p.errorReasons?.ssnMismatch ||
        fallback.errorReasons?.ssnMismatch ||
        p.errorDescription ||
        fallback.errorDescription ||
        "We could not verify your identity. Please try again later."
      : reason === "ssn_already_used"
      ? p.errorReasons?.ssnAlreadyUsed ||
        fallback.errorReasons?.ssnAlreadyUsed ||
        p.errorDescription ||
        fallback.errorDescription ||
        "We could not verify your identity. Please try again later."
      : reason === "missing_registered_ssn"
      ? p.errorReasons?.missingRegisteredSsn ||
        fallback.errorReasons?.missingRegisteredSsn ||
        p.errorDescription ||
        fallback.errorDescription ||
        "We could not verify your identity. Please try again later."
      : p.errorDescription ||
        fallback.errorDescription ||
        "We could not verify your identity. Please try again later.";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="w-full max-w-md backdrop-blur">
        <CardContent className="p-8">
          <div className="flex flex-col items-center justify-center space-y-6">
            {isSuccess ? (
              <>
                <div className="relative">
                  <CheckCircle2 className="h-20 w-20 text-green-500 animate-in zoom-in duration-500" />
                  <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30" />
                  <div className="absolute inset-0 bg-green-500/20 rounded-full animate-pulse" />
                </div>
                <div className="text-center space-y-2">
                  <h1 className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {p.successTitle ??
                      fallback.successTitle ??
                      "Verification successful!"}
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {p.successDescription ??
                      fallback.successDescription ??
                      "Your identity has been successfully verified."}
                  </p>
                </div>
              </>
            ) : isError ? (
              <>
                <XCircle className="h-20 w-20 text-red-500 animate-in zoom-in duration-500" />
                <div className="text-center space-y-2">
                  <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {p.errorTitle ??
                      fallback.errorTitle ??
                      "Verification failed"}
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {errorMessage}
                  </p>
                </div>
                <Button
                  onClick={() => {
                    void (async () => {
                      try {
                        const { isNativeCapacitorApp } = await import(
                          "@/integrations/clients/carAuction/nativeAuth"
                        );
                        if (isNativeCapacitorApp()) {
                          window.location.hash = "#/dashboard";
                          return;
                        }
                      } catch {
                        /* ignore */
                      }
                      window.close();
                      window.location.href = "/";
                    })();
                  }}
                  className="w-full"
                >
                  {p.returnToApp ?? fallback.returnToApp ?? "Tillbaka till appen"}
                </Button>
              </>
            ) : (
              <>
                <Loader2 className="h-20 w-20 text-blue-500 animate-spin" />
                <div className="text-center space-y-2">
                  <h1 className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                    {p.verifyingTitle ??
                      fallback.verifyingTitle ??
                      "Verifying..."}
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {p.verifyingMessage ??
                      fallback.verifyingMessage ??
                      "Waiting for verification result."}
                  </p>
                </div>
              </>
            )}

            {isSuccess && countdown > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-500">
                {closingText}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DiditSuccess;
